import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { getBooks, getTeacherClasses } from "../services/api";
import { classesForBook, classesForUpload } from "../data/examBlock";
import { CreateClassModal } from "../components/CreateClassModal";
import {
  appendCreatedClass,
  bookNeedsActivation,
} from "../utils/classCreation";
import {
  submitBatch,
  type BatchStudent,
  type BatchItemProgress,
} from "../services/batchApi";
import { useBatchQuota } from "../hooks/useBatchQuota";
import { quotaLabel, quotaExhausted, isDailyLimitError, DAILY_LIMIT_MESSAGE } from "../utils/batchQuota";
import type { Book, classDataProps } from "../types/common";
import { getUserData } from "../utils/userData";
import {
  compressImageForUpload,
  resolveInitialAnnotation,
  truncateFilename,
} from "../utils/batchImageUtils";
import {
  expandFilesWithPdf,
  type ExpandError,
} from "../utils/pdfToImages";
import {
  screenUploadFiles,
  type RejectedFile,
} from "../utils/batchFileScreen";
import { bakeRotationIntoImage, buildRotatedSource } from "../utils/imageUtils";
import {
  canGoNextImage,
  nextImageLabel,
  prevImageLabel,
  thumbState,
  clampImageIndex,
  thumbStripScrollLeft,
  measureThumbLeft,
} from "../utils/batchAnnotateNav";
import {
  collectBatchPrepTasks,
  prepareBatchFiles,
  buildBatchStudentFiles,
} from "../utils/batchSubmitPrep";
import {
  allSeatsFromFilename,
  prefillSeatNumbers,
  seatGateSatisfied,
  buildStudentGroups,
  cascadeSeatNumbers,
  type StudentGroup,
} from "../utils/batchSeatAssign";
import { buildBatchUploadTeacherState } from "../utils/teacherReturnNav";

// 高中班級批次上傳精靈(kanban 106957)
// 動線移植國中「批次全班作文批改」(快速模式);高中入口自「批改＆檢視紀錄」
// 的題目列進入(帶 bookId/chapterId),班級用老師既有班級。
// v1 不含透視校正步驟(國中 STEPS_FAST 等價),校正列 v2。
// kanban 1000217:「確認頁序」步驟移除 —— 座號於步驟 4 由檔名判別帶入
// (詳 utils/batchSeatAssign),同座號多張圖的面序由 buildStudentGroups 的
// 現行排序規則決定,確認座號後直接進送出。
const STEPS = [
  { key: "class", label: "選擇班級" },
  { key: "topic", label: "確認題目" },
  { key: "images", label: "上傳圖片" },
  { key: "annotate", label: "確認座號" },
  { key: "submit", label: "送出批改" },
] as const;

const MAX_IMAGES = 40;

interface UploadedImage {
  file: File;
  preview: string;
  filename: string;
  // 檢視旋轉角度(0/90/180/270)。預覽區只轉畫面(步驟 3、4 縮圖用 CSS transform,
  // 步驟 5 大圖用 canvas 重畫),交卷時才由 bakeRotationIntoImage 烤進圖檔像素
  // 送出(kanban 113663;原 106078 僅檢視用)。
  // 角度刻意存在 image 物件上、不用平行陣列:頁序調換與刪圖都不會錯位。
  rotation?: number;
  // 是否為 PDF 展開頁(expandFilesWithPdf 的 fromPdf 平行陣列在上傳當下記到
  // 物件上):逐張確認的判斷(allSeatsFromFilename)要用,存在物件上
  // 讓刪圖後仍與圖片對得上(kanban 1000217)。
  fromPdf?: boolean;
  // 上傳當下座號是否由檔名判別成功(同 fromPdf 的做法記在物件上)。
  // 判斷必須看這個、不能看 annotation 目前的 seatNumber:老師補填過
  // 一輪後每列座號都非空,拿當下值判斷會把「回步驟 3 刪一張再前進」誤判成
  // 檔名例外 —— 座號留著跳號、gate 與徽章全消失(code review must-fix)。
  seatFromFilename?: boolean;
}

// 座號來源只剩檔名判別與老師手填(kanban 1000217 停用 AI 座號辨識後),
// 原本的 id/source/confidence/detectPending 欄位隨辨識佇列一併移除。
interface Annotation {
  seatNumber: string;
  name: string;
  pageNumber: number | null;
}

/**
 * 大圖預覽:用 canvas 畫「已套用旋轉」的畫面(kanban 113663)。
 * (原本供步驟 4 與步驟 5「確認頁序」共用;頁序步驟移除後只剩步驟 4 用,
 * kanban 1000217。)
 *
 * 為什麼不沿用步驟 3、4 縮圖的 <img> + CSS transform:transform 不改變 layout box。
 * 這一格是 object-contain、外層又是 overflow-hidden,
 * 直接套 rotate(90deg) 只會把轉出去的部分裁掉。buildRotatedSource 在 90/270 時
 * 已把 canvas 寬高互換,瀏覽器會以互換後的尺寸重新 fit 進同一個容器 ——
 * 不裁切,也不必動任何容器尺寸。
 *
 * 這格非補旋轉不可:113663 之後角度會真的烤進送出的像素,確認座號的大圖是
 * 老師送出前的最後一眼;這裡還是歪的,老師會以為旋轉鈕沒作用。
 *
 * 失敗一律留白(圖載不進來、取不到 2d context):預覽畫不出來不該擋住整個步驟。
 */
const RotatedFacePreview: React.FC<{
  src: string;
  angle: number;
  label: string;
  className: string;
}> = ({ src, angle, label, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (cancelled || !canvas) return;
      // 每次都從原圖重算(而非在上一次結果上再轉):連按 4 次能精確回正、畫質不累積損失
      const source = buildRotatedSource(img, angle);
      // canvas 沒有 naturalWidth/naturalHeight,退回 width/height
      const width = ("naturalWidth" in source ? source.naturalWidth : 0) || source.width;
      const height =
        ("naturalHeight" in source ? source.naturalHeight : 0) || source.height;
      if (!width || !height) return;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(source, 0, 0);
    };
    img.src = src;
    return () => {
      // 換圖/換角度/離開步驟時,舊的 onload 不可再往(可能已被重用的)canvas 上畫
      cancelled = true;
    };
  }, [src, angle]);

  // canvas 沒有 alt,用 role+aria-label 補上與原本 <img alt> 等價的資訊
  return (
    <canvas ref={canvasRef} role="img" aria-label={label} className={className} />
  );
};

const BatchUpload: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get("bookId") || "";
  const chapterId = searchParams.get("chapterId") || "";
  const chapterName = searchParams.get("chapterName") || "";
  // 章節真正的 _id(非章節名稱),BookPreview 額外多帶的參數(kanban 106957 code review)。
  // 只用來寫回「前往檢視紀錄」的 teacherState.selectedChapter,不影響 chapterId 既有的
  // 後端 key 用途(submitBatch 仍用 chapterId)。
  const chapterUid = searchParams.get("chapterUid") || "";

  const userData = getUserData();
  const { sub, email, name } = userData;

  const [currentStep, setCurrentStep] = useState(0);
  // 步驟 4 一次只顯示一張,這是當前看的是第幾張(kanban 106957,比照國中)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const seatInputRef = useRef<HTMLInputElement>(null);
  const thumbStripRef = useRef<HTMLDivElement>(null);
  const currentThumbRef = useRef<HTMLButtonElement>(null);
  const [classes, setClasses] = useState<classDataProps[]>([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(true);
  const [selectedClass, setSelectedClass] = useState<classDataProps | null>(
    null
  );
  // 建立新班級(kanban 106957):開通碼欄位判斷需要書籍資料,精靈 URL 只有 bookId
  const [books, setBooks] = useState<Book[]>([]);
  const [isBooksReady, setIsBooksReady] = useState(false);
  const [showCreateClass, setShowCreateClass] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [imageLoadingProgress, setImageLoadingProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  // 超過 40 張改為自動保留前面的張數(kanban 1000645):記錄本批實際加入與捨棄的張數
  const [uploadLimitWarning, setUploadLimitWarning] = useState<{
    kept: number;
    dropped: number;
  } | null>(null);
  // 同一個提示區列兩種訊息(kanban 1000553):PDF 展開失敗(ExpandError)與
  // 收檔時被略過的檔案(RejectedFile,格式不符或超過大小門檻)。兩者都只保證
  // 有 name 與 reason,渲染只用到這兩個欄位。
  const [pdfErrors, setPdfErrors] = useState<(ExpandError | RejectedFile)[]>(
    []
  );

  // 逐張確認(kanban 1000217;座號自動編號已於 2026-08-26 依 PM 需求移除):
  // seatConfirmRequired = 本輪進入步驟 4 時有檔名判別不出的圖,需老師逐張確認。
  // dirty flag:檔案清單有變動(增刪檔,含初始)時為 true,進步驟 4 時才
  // 重新判斷/編號並清掉;清單未變時不重跑(保留老師手改)。
  const seatAssignDirtyRef = useRef(true);
  const [seatConfirmRequired, setSeatConfirmRequired] = useState(false);
  // 逐張確認 gate(僅在有檔名判別不出的圖時啟用):看過哪些張記在這裡,目前顯示那張算看過
  const [visitedIndices, setVisitedIndices] = useState<Set<number>>(
    () => new Set()
  );
  // 老師手改座號後往後順排(kanban 1000217,PM 2026-08-26 追加):這裡記
  // 「這次編輯開始前那張的座號」,離開輸入框/切換到別張時才拿來比對並順排。
  // 不在每次按鍵就排 —— 打「12」的中途會先看到 1,後面整批被排成 2、3、4…,
  // 老師還沒打完就被閃一輪。同一張連改多次只留第一次的原值,以最後結果為準。
  const seatEditRef = useRef<{ index: number; before: string } | null>(null);

  const [groups, setGroups] = useState<StudentGroup[]>([]);

  // 交卷前的圖片處理進度(烤旋轉)。整班數十張要跑一陣子,
  // 沒有提示老師會以為當掉而重按送出(kanban 113663)
  const [prepareProgress, setPrepareProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [submitProgress, setSubmitProgress] = useState<BatchItemProgress[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 批次上傳每日剩餘次數（kanban 1000892，比照國中）
  const { quota: batchQuota, refresh: refreshBatchQuota } = useBatchQuota(getUserData()?.sub);
  const batchQuotaLabel = quotaLabel(batchQuota);
  const [submitDone, setSubmitDone] = useState(false);

  // 教師身份檢查(同 BookPreview)
  useEffect(() => {
    const userDataString = localStorage.getItem(
      "oidc.user:https://id.hle.com.tw:js"
    );
    if (!userDataString) {
      navigate("/");
      return;
    }
    try {
      const parsed = JSON.parse(userDataString);
      const userRole =
        parsed.profile?.role || parsed.profile?.["custom:role"] || "學生";
      if (userRole !== "老師") {
        navigate("/");
      }
    } catch {
      navigate("/");
    }
  }, [navigate]);

  // 載入老師在此書籍下的班級
  useEffect(() => {
    if (!sub) return;
    (async () => {
      setIsLoadingClasses(true);
      try {
        const res = await getTeacherClasses(sub);
        const all: classDataProps[] = Array.isArray(res.data) ? res.data : [];
        // 選班清單與檢視紀錄、派出作文一致,學測/模考沿用陪練國寫舊班(kanban 106957)。
        //
        // 8/4 PM 回報「不該看到其他書籍的已派班級」後，這裡曾改為 classesForUpload
        // (嚴格只認本書)，但派出作文(BookPreview)與檢視紀錄(ClassFeedback)都沒跟著改,
        // 三個入口變成兩種答案:老師在派出作文把模考題目派給陪練國寫舊班,回到批次上傳
        // 卻選不到同一個班,清單顯示「尚未建立班級」;重建一個新班上傳後,檢視紀錄又同時
        // 列出新舊兩批班。8/26 PM 回報此矛盾,確認改回與其他兩處一致(兩本試題書會列出
        // 同一批陪練國寫班級,PM 已知悉)。
        //
        // 選到舊班不會讓紀錄落錯書:送單帶的 hs.bookId 是當下這本書、不是班級原本綁的書,
        // worker 也以它寫回(ai_correction_service.py),檢視紀錄用同一組條件查得到。
        setClasses(classesForBook(all, bookId));
      } catch (error) {
        console.error("載入班級失敗:", error);
        setClasses([]);
      } finally {
        setIsLoadingClasses(false);
      }
    })();
  }, [sub, bookId]);

  // 換張後把焦點移到座號欄。不加的話:按「下一張」→ 切到下一張 → 那張座號還沒填
  // → 按鈕自己變 disabled → 瀏覽器把焦點丟回 body,鍵盤使用者要從整頁最上方
  // (Header、麵包屑、六顆步驟膠囊)重新 Tab 回來,每張一次。改版前一次列出所有列,
  // Tab 可以一路掃過去,這是單張化引進的迴歸。
  useEffect(() => {
    if (STEPS[currentStep].key !== "annotate") return;
    seatInputRef.current?.focus({ preventScroll: true });
    // 同時把當前縮圖捲進可視範圍:縮圖列溢出後(桌機約 24 張起),選中框會整個
    // 捲出畫面外,老師看不出自己在第幾張
    const strip = thumbStripRef.current;
    const thumb = currentThumbRef.current;
    if (!strip || !thumb) return;
    const next = thumbStripScrollLeft(
      measureThumbLeft(strip, thumb),
      thumb.offsetWidth,
      strip.scrollLeft,
      strip.clientWidth
    );
    if (next !== null) strip.scrollLeft = next;
  }, [currentStep, currentImageIndex]);

  // 逐張確認 gate 的「看過」追蹤(kanban 1000217,比照國中 kanban 1000190):
  // 步驟 4 期間目前顯示的那張算看過 —— 進入步驟 4(第一張)與每次上一張/
  // 下一張都會落到這裡;手動改座號不影響追蹤。1 張時當下即滿足 gate。
  useEffect(() => {
    if (STEPS[currentStep].key !== "annotate") return;
    setVisitedIndices((prev) =>
      prev.has(currentImageIndex) ? prev : new Set(prev).add(currentImageIndex)
    );
  }, [currentStep, currentImageIndex]);

  // 載入書籍資料:建班 modal 需 isNeedInvition 判斷開通碼欄(kanban 106957)。
  // fail-open:取不到書就當一般書,不鎖死建立入口;若真是開通碼書,
  // 送出時由後端擋下、走 modal 的錯誤提示流程(邊界 B2)。
  useEffect(() => {
    if (!sub) return;
    (async () => {
      try {
        const res = await getBooks();
        setBooks(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("載入書籍失敗:", error);
        setBooks([]);
      } finally {
        setIsBooksReady(true);
      }
    })();
  }, [sub]);

  // 建立成功:append 進本地清單並自動選取,不重打清單 API(仿國中 handleCreateClass)
  const handleClassCreated = (cls: classDataProps) => {
    const next = appendCreatedClass(classes, cls);
    setClasses(next.classes);
    setSelectedClass(next.selected);
  };

  // ── Step 3:上傳圖片 ────────────────────────────────────────────────

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rawFiles = Array.from(e.target.files || []);
    e.target.value = "";
    if (rawFiles.length === 0) return;

    // PDF 逐頁展開成圖片(kanban 106497),頁數與圖片合併計算上限
    //
    // maxPages 明確給到「這批還能放幾張」,不用 pdfToImages 的預設 10:上限由
    // 106497 的需求定為 40 張(PDF 每頁算 1 張),單檔再夾一層 10 頁沒有需求依據,
    // 而整班掃描本來就是一份 PDF 裝全班,10 頁只裝得下 5 個人(正反面)。
    // 單篇上傳(UploadEssay)不傳這個參數、沿用預設 10,一篇作文不會有 40 頁。
    const remaining = MAX_IMAGES - uploadedImages.length;
    // 已滿 40 張:加圖入口在滿額時本來就不渲染,這裡是第二層保險 ——
    // maxPages 給到 0 會讓 PDF 展開一頁都不轉,誤報「PDF 無法讀取或轉檔失敗」
    if (remaining <= 0) return;

    // 收檔過濾(kanban 1000553):格式不符與超過大小門檻的檔案在這裡就攔下來,
    // 連同原因交給下方的提示區列出。改動前這頁沒有任何檢查 —— .txt 選得進來、
    // 被當成圖片收進清單,還被 parseFilename 拿去解析成座號姓名。
    // PDF 與圖片的門檻分開,理由見 utils/batchFileScreen 檔頭。
    const { accepted: validFiles, rejected } = screenUploadFiles(rawFiles);
    if (validFiles.length === 0) {
      // 一個檔都沒收下:不進展開流程,但提示一定要出來,否則畫面又是「什麼都沒發生」。
      // 上一批的保留/未加入提示描述的是上一次操作,一併清掉,避免與這次的訊息並排。
      setPdfErrors(rejected);
      setUploadLimitWarning(null);
      return;
    }

    setImageLoadingProgress({ done: 0, total: validFiles.length });
    const expanded = await expandFilesWithPdf(validFiles, {
      maxPages: remaining,
      onFileProgress: (done, total) => setImageLoadingProgress({ done, total }),
    });
    // 被略過的檔案與 PDF 展開錯誤合併顯示,不互相覆蓋(被略過的排前面,那是老師
    // 這次選檔的直接結果)。頁數超額的逐檔截斷訊息不顯示,超額一律由下方統一的
    // 「已保留前 N 張」提示呈現(kanban 1000645);加密/損毀等真錯誤照常列出。
    setPdfErrors([
      ...rejected,
      ...expanded.errors.filter((err) => err.code !== "truncated"),
    ]);
    let files = expanded.files;
    let fromPdf = expanded.fromPdf;
    if (files.length === 0) {
      // 一個檔都沒收下:上一批的保留/未加入提示描述的是上一次操作,一併清掉,
      // 避免與這次的錯誤訊息並排、看不出哪句在說現在
      setUploadLimitWarning(null);
      setImageLoadingProgress(null);
      return;
    }

    // 超過 40 張不再整批拒收,改為保留前面的、捨棄超出的(kanban 1000645)。
    // 單一 PDF 超長在 expandFilesWithPdf 內就截到剩餘額度(捨棄頁數記在
    // truncated 錯誤的 dropped);多檔合計超額在這裡截,保留順序＝選取順序。
    // files 與 fromPdf 必須同步截,兩陣列靠索引對應(座號判別用),錯位不得。
    const pdfDropped = expanded.errors.reduce(
      (sum, err) => sum + (err.code === "truncated" ? err.dropped ?? 0 : 0),
      0
    );
    const sliceDropped = Math.max(0, files.length - remaining);
    if (sliceDropped > 0) {
      files = files.slice(0, remaining);
      fromPdf = fromPdf.slice(0, remaining);
    }
    const dropped = pdfDropped + sliceDropped;
    setUploadLimitWarning(
      dropped > 0 ? { kept: files.length, dropped } : null
    );

    setImageLoadingProgress({ done: 0, total: files.length });
    const newImages: UploadedImage[] = [];
    const newAnnotations: Annotation[] = [];
    for (let i = 0; i < files.length; i++) {
      const { file, previewUrl } = await compressImageForUpload(files[i]);
      // PDF 展開的頁不吃檔名 —— 硬拆出來的殘渣會佔住座號/姓名,讓步驟 4 的
      // 逐張確認的判斷誤以為「檔名判別成功」(kanban 108142 的理由延續)。
      // 解析不到座號的頁不再送 AI 辨識,座號維持空白,由老師在步驟 4 逐張補上
      // (kanban 1000217 停用 AI 座號辨識、亦不自動編號)。
      const parsed = resolveInitialAnnotation(files[i].name, fromPdf[i]);
      newImages.push({
        file,
        preview: previewUrl,
        filename: files[i].name,
        // 逐張確認與順排要用的「來源」記在物件上(kanban 1000217):
        // 這張是不是 PDF 展開頁、座號是不是檔名判別出來的
        fromPdf: fromPdf[i],
        seatFromFilename: !!parsed.seatNumber,
      });
      newAnnotations.push({
        seatNumber: parsed.seatNumber,
        name: parsed.name,
        pageNumber: parsed.pageNumber,
      });
      setImageLoadingProgress({ done: i + 1, total: files.length });
    }
    setUploadedImages((prev) => [...prev, ...newImages]);
    setAnnotations((prev) => [...prev, ...newAnnotations]);
    setImageLoadingProgress(null);
    // 檔案清單變動 → 下次進步驟 4 重新判斷是否需要逐張確認(kanban 1000217)。
    // visited 由 enterAnnotateStep 在 dirty 時一併重置。
    seatAssignDirtyRef.current = true;
    // 擱著的座號編輯作廢:索引已位移,再順排會排到別張去
    seatEditRef.current = null;
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(uploadedImages[index]?.preview);
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
    setAnnotations((prev) => prev.filter((_, i) => i !== index));
    // 保留/未加入提示描述的是「上一次選檔」,清單一變動數字就過期了,清掉
    setUploadLimitWarning(null);
    // 同 handleImageUpload:清單變動即標記 dirty、作廢擱著的座號編輯
    seatAssignDirtyRef.current = true;
    seatEditRef.current = null;
  };

  // 將指定圖片順時針旋轉 90°:只改角度狀態,交卷時才烤進像素(kanban 113663)
  const rotateImage = (index: number) => {
    setUploadedImages((prev) =>
      prev.map((im, i) =>
        i === index ? { ...im, rotation: ((im.rotation || 0) + 90) % 360 } : im
      )
    );
  };

  const updateAnnotation = (index: number, patch: Partial<Annotation>) => {
    setAnnotations((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...patch } : a))
    );
  };

  // 座號編輯結束(離開輸入框或切換到別張)時,把後面的張往後順排。
  // 規則全在 cascadeSeatNumbers(已確認的張擋住、檔名判出的張保留並當新基準),
  // 這裡只負責「什麼時候算編輯結束」。姓名欄不走這條路徑。
  const commitSeatCascade = () => {
    const pending = seatEditRef.current;
    if (!pending) return;
    seatEditRef.current = null;
    setAnnotations((prev) => {
      const next = cascadeSeatNumbers({
        seatNumbers: prev.map((a) => a.seatNumber),
        images: uploadedImages,
        visitedIndices,
        changedIndex: pending.index,
        previousSeatNumber: pending.before,
      });
      if (!next) return prev;
      return prev.map((a, i) =>
        a.seatNumber === next[i] ? a : { ...a, seatNumber: next[i] }
      );
    });
  };

  // ── 分組(座號 → 學生,依頁碼排列):邏輯抽到 utils/batchSeatAssign,
  // 「確認頁序」步驟移除後(kanban 1000217)分組結果直接進送出步驟,
  // 同座號多張圖的面序由排序規則決定(pageNumber 升冪、無標記依上傳順序)。

  const buildGroups = useCallback(
    (): StudentGroup[] => buildStudentGroups(annotations),
    [annotations]
  );

  // 逐張確認 gate(kanban 1000217):僅在有檔名判別不出的圖時啟用;檔名全數
  // 判別成功的路徑不加 gate、維持現行流程。Math.min 收斂刪圖後殘留的越界索引。
  const seatConfirmedCount = Math.min(
    visitedIndices.size,
    uploadedImages.length
  );
  const seatGateOk = seatGateSatisfied(
    seatConfirmRequired,
    seatConfirmedCount,
    uploadedImages.length
  );

  // ── Step 5:送出 ────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!selectedClass || groups.length === 0) return;
    // 次數用盡二次防線（kanban 1000892）：直接進到本頁也擋，文案同國中
    if (quotaExhausted(batchQuota)) {
      setSubmitError(DAILY_LIMIT_MESSAGE);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // 交卷前處理:老師只按旋轉鈕,烤旋轉在按下送出後才跑(kanban 113663)。
      // 四角校正已搬進批改後端管線內部處理(kanban 114186),前端不再打 /cornerdetect。
      // 必須逐張 await —— 理由見 batchSubmitPrep.prepareBatchFiles 的說明。
      const prepTasks = collectBatchPrepTasks(groups, uploadedImages);
      if (prepTasks.length > 0) {
        setPrepareProgress({ done: 0, total: prepTasks.length });
      }
      const preparedFiles = await prepareBatchFiles(
        prepTasks,
        { bake: bakeRotationIntoImage },
        (done, total) => setPrepareProgress({ done, total })
      );
      setPrepareProgress(null);

      const filesPerGroup = buildBatchStudentFiles(
        groups,
        uploadedImages,
        preparedFiles
      );
      const students: BatchStudent[] = groups.map((g, index) => ({
        seatNo: g.seatNumber,
        seatNoInt: parseInt(g.seatNumber, 10) || 0,
        userName: g.name,
        files: filesPerGroup[index],
        originFiles: [],
      }));
      const result = await submitBatch(
        {
          bookId,
          chapterId,
          chapterName,
          classCode: selectedClass.classCode,
          className: selectedClass.className,
          teacherId: sub,
        },
        students,
        { onProgress: setSubmitProgress }
      );
      setSubmitProgress(result.items);
      setSubmitDone(true);
      // 送出成功扣了一次，重查讓徽章跟上（失敗就維持原值）
      void refreshBatchQuota();
    } catch (error) {
      // 後端每日次數用盡回 429：給人話，不顯示原始錯誤字串（kanban 1000892）
      setSubmitError(
        isDailyLimitError(error)
          ? DAILY_LIMIT_MESSAGE
          : error instanceof Error ? error.message : String(error)
      );
    } finally {
      setPrepareProgress(null);
      setIsSubmitting(false);
    }
  }, [
    selectedClass,
    groups,
    uploadedImages,
    bookId,
    chapterId,
    chapterName,
    sub,
    batchQuota,
    refreshBatchQuota,
  ]);

  // 前往檢視紀錄:除了原本的 URL query fallback,額外把 teacherState 寫進
  // sessionStorage,讓 ClassFeedback 初載優先還原成剛上傳的班級與題目
  // (kanban 106957)。selectedClass 理論上此刻必有值(送出批改前已檢查過),
  // 仍防禦 null——缺值就略過寫入,單靠 URL query 導頁,不讓按鈕壞掉。
  // selectedChapter 要傳章節「_id」(chapterUid),不是後端 key 用的 chapterId
  // (章節名稱)——code review 抓到的阻斷性 bug,見 buildBatchUploadTeacherState 註解。
  const handleGoToRecords = useCallback(() => {
    if (selectedClass) {
      sessionStorage.setItem(
        "teacherState",
        JSON.stringify(
          buildBatchUploadTeacherState({ bookId, chapterUid, selectedClass })
        )
      );
    }
    navigate(
      `/teacher/correcting-records?bookId=${encodeURIComponent(bookId)}&chapterId=${encodeURIComponent(chapterId)}&chapterName=${encodeURIComponent(chapterName)}`
    );
  }, [navigate, bookId, chapterId, chapterName, chapterUid, selectedClass]);

  // ── 步驟渲染 ─────────────────────────────────────────────────────────

  const renderStepClass = () => (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-gray-800">
        選擇要上傳作文的班級
      </h2>
      {isLoadingClasses ? (
        <div className="py-8 text-center text-gray-500">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          載入班級中...
        </div>
      ) : (
        <>
          {classes.length === 0 && (
            <div className="py-6 text-center text-gray-500">
              此書籍尚未建立班級,請按「建立新班級」開始
            </div>
          )}
          {/* 班級卡與「建立新班級」同一個 grid、同一種卡片形式(kanban 106957)。
              建立鈕常駐:有無班級都顯示 —— PM 2026-07-30「進入後若書籍沒有建立
              班級,預期希望有建立班級的按鈕」。 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {classes.map((cls) => (
              <button
                key={cls._id}
                onClick={() => setSelectedClass(cls)}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  selectedClass?._id === cls._id
                    ? "border-[#2D86FF] bg-blue-50"
                    : "border-gray-200 hover:border-gray-400"
                }`}
              >
                <div className="font-medium text-gray-800">{cls.className}</div>
                <div className="text-sm text-gray-500">
                  邀請碼:{cls.classCode}
                </div>
              </button>
            ))}
            <button
              onClick={() => setShowCreateClass(true)}
              disabled={!isBooksReady}
              // 用未選取樣式:藍框藍底是「已選這班」的語意,建立鈕長期掛著那個
              // 樣式會被誤讀成已選取(而「下一步」其實要選了班才會亮)
              className="p-4 border rounded-lg text-left transition-colors border-gray-200 hover:border-gray-400 disabled:opacity-50 disabled:cursor-wait"
            >
              <div className="font-medium text-gray-800">＋ 建立新班級</div>
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderStepTopic = () => (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-gray-800">確認作文題目</h2>
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="text-sm text-gray-500 mb-1">本次批改題目</div>
        <div className="text-gray-800 font-medium">{chapterName || "—"}</div>
      </div>
      <p className="text-sm text-gray-500">
        題目由「批改＆檢視紀錄」的題目列帶入,批改結果會列在該題的檢視紀錄中。
      </p>
    </div>
  );

  const renderStepImages = () => (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-gray-800">上傳作文圖片</h2>
      <p className="text-sm text-gray-500">
        檔名格式「座號_姓名_面別」(如 1_小明_1.jpg)可自動帶入座號與姓名;
        同一位學生多面稿紙請上傳多張圖片。
      </p>

      {pdfErrors.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-700 text-sm">
          {pdfErrors.map((err) => (
            <div key={err.name}>
              {err.name}:{err.reason}
            </div>
          ))}
        </div>
      )}

      {uploadLimitWarning && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-700 text-sm">
          選取的內容超過上限(最多 {MAX_IMAGES} 張,PDF 每頁算 1 張),已自動保留前{" "}
          {uploadLimitWarning.kept} 張,其餘 {uploadLimitWarning.dropped} 張未加入。
        </div>
      )}

      {imageLoadingProgress && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <span className="w-5 h-5 border-2 border-[#2D86FF] border-t-transparent rounded-full animate-spin"></span>
          <span className="text-sm text-[#254D71] font-medium">
            圖片載入中請稍候({imageLoadingProgress.done} /{" "}
            {imageLoadingProgress.total})
          </span>
        </div>
      )}

      {uploadedImages.length < MAX_IMAGES && (
        // 圖片載入(PDF 展開/壓縮)中鎖住加圖:此時 handleImageUpload 還在跑,
        // 再開第二批會讓兩次呼叫共用同一個載入進度橫幅、上限檢查讀到過期的張數
        // (kanban 106957 #7)。這段每張不到一秒,鎖了幾乎無感。
        // AI 辨識中「不再」鎖(108142 的鎖是為了 baseIndex + i 的索引寫回):
        // 改成佇列後追加的圖入列接著辨識,老師漏掃的頁不必乾等整班辨識完 ——
        // 辨識一張要打一次 API,這段才是等最久的,鎖在這裡最傷。
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            imageLoadingProgress
              ? "border-gray-200 opacity-50 cursor-not-allowed"
              : "border-gray-300 hover:border-[#2D86FF]/50 cursor-pointer"
          }`}
          onClick={() => {
            if (imageLoadingProgress) return;
            fileInputRef.current?.click();
          }}
        >
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-3 text-base text-gray-500">點擊選擇圖片或 PDF 檔案</p>
          <p className="text-sm text-gray-400 mt-1">
            {/* 「圖片」二字不能省:10MB 只限圖片,PDF 走另一條遠高的門檻
                (kanban 1000553)。PDF 的 400MB 不寫進說明 —— 那是防呆天花板,
                不是老師平常需要記的數字,寫出來反而像在鼓勵傳巨檔。
                寫「每張圖片」不寫「圖片單檔」:後者被老師讀成每次上傳合計 10MB(kanban 1001860)。 */}
            支援 JPG、PNG、PDF 格式(PDF 每頁算 1 張),每張圖片不超過 10MB,最多 {MAX_IMAGES} 張
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            multiple
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
      )}

      {uploadedImages.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {uploadedImages.map((img, index) => (
            <div
              key={img.preview}
              className="relative group rounded-lg overflow-hidden border border-gray-200 bg-white"
            >
              <img
                src={img.preview}
                alt={`作文圖片 ${index + 1}`}
                className="w-full h-32 object-cover transition-transform duration-200 ease-out"
                style={{ transform: `rotate(${img.rotation || 0}deg)` }}
              />
              {/* 刪除鈕(右上,常駐)— hover 才顯示在觸控裝置上完全按不到,
                  尺寸放大到 w-8 h-8 當可按的觸控目標,並補 aria-label。
                  (原本 AI 辨識中要鎖住;辨識停用後不再需要,kanban 1000217) */}
              <button
                onClick={() => removeImage(index)}
                aria-label={`移除作文圖片 ${index + 1}`}
                className="absolute top-1 right-1 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm shadow disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ×
              </button>
              {/* 這裡刻意不放旋轉鈕 —— 入口統一在步驟 4「確認座號」大圖下緣,
                  與國中批次上傳快速模式一致(chaiwriting-j batch-upload.js,
                  kanban 106078 依 PM 附圖只留 step 4)。縮圖仍會套用 rotation
                  顯示,老師在步驟 4 轉完回到這裡看得到結果。 */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 text-center truncate">
                {truncateFilename(img.filename, 18)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // 步驟 4「確認座號」:一次一張大圖 + 下方縮圖列切換,版面比照國中批次上傳
  // (chaiwriting-j pages/batch-upload.js renderStep4,kanban 106957)。
  // PM 2026-08-04 退回原因:原本每張只有 64px 縮圖且 object-cover,稿紙上緣的
  // 手寫座號被裁掉,老師看不到就填不了必填的座號。大圖一律 object-contain。
  const renderStepAnnotate = () => {
    if (uploadedImages.length === 0) return null;
    const index = clampImageIndex(currentImageIndex, uploadedImages.length);
    const img = uploadedImages[index];
    const ann = annotations[index];
    const total = uploadedImages.length;
    const seatInvalid = !/^\d+$/.test(ann.seatNumber);
    const goToImage = (i: number) => {
      // 點「下一張」/縮圖時 onBlur 一定已經跑過(滑鼠按下就失焦),所以這裡
      // 通常是空操作(commitSeatCascade 進來就把 ref 清掉了)。留著是防禦:
      // 日後若加了不經過失焦的切張路徑(快捷鍵之類),順排才不會默默漏收。
      commitSeatCascade();
      setCurrentImageIndex(clampImageIndex(i, total));
    };

    const navBtn = (disabled: boolean, primary: boolean, extra: string) =>
      `${extra} px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        disabled
          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
          : primary
            ? "bg-[#254D71] text-white hover:bg-[#1e3a54]"
            : "bg-white text-[#254D71] border border-[#254D71] hover:bg-[#254D71]/5"
      }`;

    const atFirst = index === 0;
    const canNext = canGoNextImage(ann.seatNumber, index, total);
    const filledCount = annotations.filter((a) =>
      /^\d+$/.test(a.seatNumber.trim())
    ).length;

    return (
      <div>
        <h2 className="text-lg font-bold text-white bg-[#254D71] px-4 py-2 rounded-md mb-3">
          確認座號
        </h2>
        <p className="text-sm text-gray-500 mb-3">
          座號必填(相同座號的圖片會視為同一位學生的多面稿紙);姓名選填。
        </p>

        {/* 大圖預覽:必須用 canvas 而非 <img> + CSS transform —— 理由見
            RotatedFacePreview 的註解(transform 不改 layout box,旋轉後會被裁) */}
        <div className="flex justify-center mb-3">
          {/* minHeight 不可拿掉:canvas 在圖片 onload 前是 HTML 預設的 300x150,
              沒有下界的話每次換圖整個版面會先塌約 210px 再彈回,座號欄與「下一張」
              會在滑鼠底下移位。也不要加 key —— RotatedFacePreview 內部已依
              [src, angle] 重畫,角度變動走的就是這條重畫路徑;強制重掛載才是
              塌陷的來源,加了 key 按旋轉會整頁跳動。 */}
          <div
            className="relative rounded-lg border-2 border-gray-300 bg-gray-100 flex items-center justify-center p-2"
            style={{ minHeight: 376, maxHeight: 400 }}
          >
            <RotatedFacePreview
              src={img.preview}
              angle={img.rotation || 0}
              label={`第 ${index + 1} 張,共 ${total} 張`}
              className="max-h-[360px] max-w-full object-contain rounded"
            />
            {/* 旋轉鈕:圓形圖示鍵疊在大圖下緣置中,位置與尺寸比照國中快速模式
                (chaiwriting-j batch-upload.js 的 step 4 旋轉鈕,依 PM 設計圖)。
                角度只改狀態,交卷時才烤進送出的像素(kanban 113663)。
                提示文字用自製 tooltip、不用原生 title:原生 title 在觸控裝置不顯示。 */}
            <div className="group/rotate absolute bottom-3 left-1/2 -translate-x-1/2">
              <button
                type="button"
                onClick={() => rotateImage(index)}
                aria-label="向右旋轉90度"
                className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-[#2D86FF] transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8M21 3v5h-5"
                  />
                </svg>
              </button>
              <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-sm opacity-0 group-hover/rotate:opacity-100 transition-opacity z-10">
                向右旋轉 90°
              </span>
            </div>
          </div>
        </div>

        {/* 下方操作區 */}
        {/* 負邊距要跟著「高中」卡片的 p-4 md:p-6 走。國中卡片是 p-4 sm:p-8,
            直接照抄 sm:-mx-8 會在 640px 以上讓這條白底橫條突出卡片(640-767 各
            16px、768 以上各 8px),壓過圓角。 */}
        <div className="bg-white border-t border-gray-200 pt-3 -mx-4 px-4 md:-mx-6 md:px-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <button
              onClick={() => goToImage(index - 1)}
              disabled={atFirst}
              className={navBtn(atFirst, false, "hidden sm:block")}
            >
              {prevImageLabel(index)}
            </button>

            <div className="flex items-end gap-4 flex-1 justify-end">
              {/* 檔名膠囊(標籤已移到大圖下方,見上) */}
              <div className="hidden sm:flex items-center gap-2 min-w-0 bg-gray-100 px-3 py-1.5 rounded-lg">
                <svg
                  className="w-4 h-4 shrink-0 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span
                  className="truncate text-sm text-gray-600 max-w-[180px]"
                  title={img.filename}
                >
                  {truncateFilename(img.filename, 20)}
                </span>
              </div>

              <div className="flex flex-col gap-0.5">
                <label
                  htmlFor="annotate-seat"
                  className="text-[11px] font-medium text-gray-500 leading-none"
                >
                  座號 <span className="text-red-500">*</span>
                </label>
                <input
                  id="annotate-seat"
                  ref={seatInputRef}
                  type="text"
                  inputMode="numeric"
                  aria-required="true"
                  aria-invalid={seatInvalid}
                  value={ann.seatNumber}
                  onChange={(e) => {
                    // 這次編輯開始前的原值只記一次(連改多次不累積),
                    // 順排延到 onBlur / 切張時才跑
                    if (seatEditRef.current?.index !== index) {
                      seatEditRef.current = {
                        index,
                        before: ann.seatNumber,
                      };
                    }
                    updateAnnotation(index, {
                      seatNumber: e.target.value.replace(/\D/g, ""),
                    });
                  }}
                  onBlur={commitSeatCascade}
                  placeholder="輸入座號"
                  className={`w-24 px-3 py-1.5 border rounded-lg text-sm text-center font-medium outline-none focus:ring-2 focus:ring-[#254D71]/20 focus:border-[#254D71] ${
                    seatInvalid ? "border-red-400 bg-red-50" : "border-gray-300"
                  }`}
                />
              </div>

              <div className="flex flex-col gap-0.5">
                <label
                  htmlFor="annotate-name"
                  className="text-[11px] font-medium text-gray-500 leading-none"
                >
                  姓名 <span className="text-gray-400">(選填)</span>
                </label>
                <input
                  id="annotate-name"
                  type="text"
                  value={ann.name}
                  onChange={(e) =>
                    updateAnnotation(index, { name: e.target.value })
                  }
                  placeholder="輸入姓名"
                  className="w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#254D71]/20 focus:border-[#254D71]"
                />
              </div>
            </div>

            <button
              onClick={() => goToImage(index + 1)}
              disabled={!canNext}
              className={navBtn(!canNext, true, "hidden sm:block")}
            >
              {nextImageLabel(ann.seatNumber, index, total)}
            </button>

            {/* 手機版上下張 */}
            <div className="flex gap-2 w-full sm:hidden">
              <button
                onClick={() => goToImage(index - 1)}
                disabled={atFirst}
                className={navBtn(atFirst, false, "flex-1")}
              >
                {prevImageLabel(index)}
              </button>
              <button
                onClick={() => goToImage(index + 1)}
                disabled={!canNext}
                className={navBtn(!canNext, true, "flex-1")}
              >
                {nextImageLabel(ann.seatNumber, index, total)}
              </button>
            </div>
          </div>

          {/* 已填幾張:沒有這個數字,40 張裡漏填的那一張只能靠在縮圖列裡找灰框
              (國中把同義的「已標記座號」放在步驟外的導覽列) */}
          <div className="mt-3 text-xs text-gray-500">
            已填座號 {filledCount} / {total}
            {filledCount < total && (
              <span className="text-amber-600 ml-2">
                尚有 {total - filledCount} 張未填,填完才能進入下一步
              </span>
            )}
          </div>

          {/* 逐張確認 gate 未滿足的提示(kanban 1000217,樣式比照國中 1000190):
              有檔名判別不出的圖時,老師須逐張看過才能過「下一步」的關卡 */}
          {seatConfirmRequired && !seatGateOk && (
            <div className="mt-2">
              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded">
                請逐張點擊「下一張」確認座號(已確認 {seatConfirmedCount}/
                {total} 張)
              </span>
            </div>
          )}

          {/* 縮圖導航(kanban 1000217):逐張確認路徑加下緣座號徽章,右上狀態
              兩段式 —— 看過該張才轉綠勾,未看過顯示黃色「!」待確認(全部轉綠
              的時刻即 gate 解鎖時刻);檔名全中的路徑維持原本 thumbState 三態。 */}
          <div
            ref={thumbStripRef}
            className="mt-2 flex gap-1.5 overflow-x-auto pb-1"
          >
            {uploadedImages.map((thumb, i) => {
              const state = thumbState(i, index, annotations[i]);
              const confirmed = visitedIndices.has(i);
              const border = seatConfirmRequired
                ? i === index
                  ? "border-[#254D71] ring-2 ring-[#254D71]/30"
                  : confirmed
                    ? "border-green-400"
                    : "border-amber-400"
                : {
                    current: "border-[#254D71] ring-2 ring-[#254D71]/30",
                    done: "border-green-400",
                    empty: "border-gray-200",
                  }[state];
              // 下緣座號徽章:僅逐張確認路徑顯示,值綁該張目前座號
              //(老師手改與順排都即時反映,空值不顯示)
              const seatBadge = seatConfirmRequired
                ? (annotations[i]?.seatNumber || "").trim()
                : "";
              const title = seatConfirmRequired
                ? `第 ${i + 1} 張${confirmed ? "(已確認)" : "(待確認)"}`
                : `第 ${i + 1} 張${
                    { current: "", done: "(已填座號)", empty: "(未填座號)" }[
                      state
                    ]
                  }`;
              return (
                <button
                  key={thumb.preview}
                  ref={i === index ? currentThumbRef : undefined}
                  onClick={() => goToImage(i)}
                  aria-current={i === index}
                  title={title}
                  className={`relative rounded overflow-hidden border-2 transition-colors flex-shrink-0 ${border}`}
                  style={{ width: 44, height: 32 }}
                >
                  <img
                    src={thumb.preview}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {seatBadge && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-blue-500 text-white text-[9px] leading-[14px] text-center font-medium pointer-events-none">
                      {seatBadge}
                    </span>
                  )}
                  {(seatConfirmRequired ? confirmed : state === "done") ? (
                    <div
                      role="img"
                      // 與 title 一致:逐張確認路徑是「看過即確認」,
                      // 檔名全中的路徑綠勾表示的是「已填座號」
                      aria-label={seatConfirmRequired ? "已確認" : "已填座號"}
                      className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-bl flex items-center justify-center"
                    >
                      <svg
                        className="w-2 h-2 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={4}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  ) : seatConfirmRequired ? (
                    <div
                      role="img"
                      aria-label="待確認"
                      className="absolute top-0 right-0 w-3 h-3 bg-amber-400 rounded-bl flex items-center justify-center"
                    >
                      <span className="text-white text-[9px] leading-none font-bold">
                        !
                      </span>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderStepSubmit = () => (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-gray-800">送出批改</h2>
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600 flex flex-col gap-1">
        <span>班級:{selectedClass?.className}</span>
        <span>題目:{chapterName}</span>
        <span>
          學生 {groups.length} 位、共 {uploadedImages.length} 張圖片
        </span>
      </div>

      {submitError && (
        <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-red-600 text-sm">
          {submitError}
        </div>
      )}

      {/* 交卷前的圖片處理進度,沿用步驟 3 的載入提示樣式 */}
      {prepareProgress && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <span className="w-5 h-5 border-2 border-[#2D86FF] border-t-transparent rounded-full animate-spin"></span>
          <span className="text-sm text-[#254D71] font-medium">
            圖片處理中請稍候({prepareProgress.done} / {prepareProgress.total})
          </span>
        </div>
      )}

      {submitProgress.length > 0 && (
        <ul className="flex flex-col gap-2">
          {submitProgress.map((p) => (
            <li
              key={p.seatNo}
              className="flex items-center justify-between p-3 border border-gray-200 rounded"
            >
              <span>
                {p.seatNo} 號 {p.userName}
              </span>
              <span
                className={
                  p.status === "submitted"
                    ? "text-green-600"
                    : p.status === "failed"
                      ? "text-red-500"
                      : "text-gray-500"
                }
              >
                {p.status === "submitted"
                  ? "已送出"
                  : p.status === "failed"
                    ? `失敗:${p.error || ""}`
                    : p.status === "uploading"
                      ? "上傳中..."
                      : "等待中"}
              </span>
            </li>
          ))}
        </ul>
      )}

      {submitDone ? (
        <div className="flex flex-col items-center gap-3">
          <p className="text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm w-full text-center">
            已送出批改!AI 批改需要幾分鐘,完成後會出現在此題的「檢視紀錄」。
          </p>
          <button
            onClick={handleGoToRecords}
            className="px-8 py-3 bg-[#2D86FF] text-white rounded-full hover:bg-blue-600 transition-colors"
          >
            前往檢視紀錄
          </button>
        </div>
      ) : (
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || groups.length === 0}
          className="px-8 py-3 bg-[#2D86FF] text-white rounded-full disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors mx-auto"
        >
          {isSubmitting
            ? "送出中..."
            : `送出 ${groups.length} 位學生的作文`}
        </button>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (STEPS[currentStep].key) {
      case "class":
        return renderStepClass();
      case "topic":
        return renderStepTopic();
      case "images":
        return renderStepImages();
      case "annotate":
        return renderStepAnnotate();
      case "submit":
        return renderStepSubmit();
    }
  };

  const canGoNext = () => {
    switch (STEPS[currentStep].key) {
      case "class":
        return selectedClass !== null;
      case "topic":
        return true;
      case "images":
        return uploadedImages.length > 0 && !imageLoadingProgress;
      case "annotate":
        // seatGateOk:有檔名判別不出的圖時須逐張看過才能離開(kanban 1000217)
        return (
          annotations.every((a) => /^\d+$/.test(a.seatNumber)) && seatGateOk
        );
      default:
        return false;
    }
  };

  // 進入步驟 4「確認座號」的統一入口(kanban 1000217,比照國中 enterStep4):
  // 檔案清單自上次套用後未變動(dirty flag 未被增刪檔打開)→ 不重新判斷
  // (保留老師手改、visited 進度沿用);有變動(含首次進入)→ 重新判斷是否需要
  // 逐張確認。
  //
  // 座號規則(kanban 1000217,兩次需求修正疊出來的現況):
  //   檔名判得出的張(seatFromFilename)→ 保留檔名帶進來的值,一律不覆寫。
  //     PM 2026-08-26:「部分檔案未依格式命名時,不應影響其他符合格式檔案的
  //     座號自動帶入」—— 原本只要有一張判不出就整批覆寫成順號,老師混傳幾張
  //     沒改檔名的相機原檔,連檔名帶對的號碼都被蓋掉。
  //   檔名判不出的張(PDF 展開頁、命名不合格式)→ prefillSeatNumbers 依上傳
  //     順序預填,遇到檔名判出的張以它為新基準續排。
  //     PM 2026-08-27:整批空白、要手動填第一張才會往後排,「預期是一進入就
  //     自動帶入順號」。
  // 兩條並存:8/26 拿掉的是「連檔名判出的一起覆寫」,不是「不准填」。
  //
  // 預填只在 dirty(檔案清單有增刪)時跑,不會回頭蓋掉老師這一輪手改的值;
  // 老師改某一張之後的往後順排是另一條路徑(commitSeatCascade),那條會停在
  // 他已確認過的張。
  const enterAnnotateStep = () => {
    // 上一輪沒收掉的座號編輯一律作廢,不讓順排跨步驟殘留
    seatEditRef.current = null;
    if (seatAssignDirtyRef.current) {
      seatAssignDirtyRef.current = false;
      setVisitedIndices(new Set());
      // 檔名沒全中 → 啟用逐張確認,讓老師逐張看過並補上空的座號
      setSeatConfirmRequired(!allSeatsFromFilename(uploadedImages));
      // 檔名判別不出的張依上傳順序預先填號(kanban 1000217,PM 2026-08-27)。
      // 檔名判出的張保留原值並當新基準,8/26「不得覆寫檔名帶進來的座號」仍成立。
      setAnnotations((prev) => {
        const next = prefillSeatNumbers(prev.map((a) => a.seatNumber));
        if (!next) return prev;
        return prev.map((a, i) =>
          a.seatNumber === next[i] ? a : { ...a, seatNumber: next[i] }
        );
      });
    }
    // 一律從第一張看起(圖片可能在上一步被增刪)
    setCurrentImageIndex(0);
  };

  const goNext = () => {
    if (STEPS[currentStep].key === "images") {
      enterAnnotateStep();
    }
    // 離開確認座號步驟時依最新座號標注重建分組(直接進送出,kanban 1000217)
    // 這裡刻意不收 pending 的順排:「下一步」按得動就代表 canGoNext 過了,
    // 而 gate 開啟時那要求每張都已 visited(順排從下一格就 break)、gate 沒開
    // 則代表每張座號都來自檔名(順排每格都跳過),兩種情形順排都必定是空操作。
    if (STEPS[currentStep].key === "annotate") {
      setGroups(buildGroups());
    }
    setCurrentStep((s) => s + 1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F7FA]">
      <Header />
      <main className="flex-grow md:px-xl px-sm py-md max-w-2xl mx-auto w-full">
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 flex flex-col gap-6">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-xl font-bold text-gray-800">上傳班級作文</h1>
            {batchQuotaLabel != null && (
              <span className="text-xs border border-[#254D71] text-[#254D71] rounded px-2 py-0.5 whitespace-nowrap">
                今日剩餘次數：{batchQuotaLabel}
              </span>
            )}
          </div>

          <ol className="flex flex-wrap gap-2">
            {STEPS.map((step, index) => (
              <li
                key={step.key}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                  index === currentStep
                    ? "bg-[#2D86FF] text-white"
                    : index < currentStep
                      ? "bg-blue-100 text-[#2D86FF]"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                <span>{index + 1}</span>
                <span className="hidden sm:inline">{step.label}</span>
              </li>
            ))}
          </ol>

          {renderStepContent()}

          <div className="flex justify-between border-t border-gray-100 pt-4">
            <button
              onClick={() =>
                currentStep === 0
                  ? navigate(-1)
                  : setCurrentStep((s) => s - 1)
              }
              disabled={isSubmitting || submitDone}
              className="px-6 py-2 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              {currentStep === 0 ? "返回" : "上一步"}
            </button>
            {currentStep < STEPS.length - 1 && (
              <button
                onClick={goNext}
                disabled={!canGoNext()}
                className="px-6 py-2 bg-[#2D86FF] text-white rounded-full disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
              >
                下一步
              </button>
            )}
          </div>
        </div>
      </main>
      <Footer />

      {/* 建立新班級 Modal(與派出作文共用元件;kanban 106957)
          existingClasses 刻意只給本書自己的班:重名檢查的用意是「同一本書內同名班會
          讓老師自己看混」,跨書同名早就不會混資料(容器唯一性是 creatorID+bookID+
          classCode,不看班名,見 class_service.create_class)。清單放寬後若連帶把比對
          放寬,陪練國寫已有的班名會擋住這本書建同名班 —— 對老師是無法理解的限制。 */}
      {showCreateClass && (
        <CreateClassModal
          bookId={bookId}
          needActivation={bookNeedsActivation(books, bookId)}
          teacherId={sub}
          teacherEmail={email}
          teacherName={name}
          existingClasses={classesForUpload(classes, bookId)}
          onCreated={handleClassCreated}
          onClose={() => setShowCreateClass(false)}
        />
      )}
    </div>
  );
};

export default BatchUpload;
